import type { RumGlobal } from '@datadog/browser-rum'
import type { BackOffHandler } from '@epilot/journey-logic-commons/src/utils/backOff'
import { backOffFibonacci } from '@epilot/journey-logic-commons/src/utils/backOff'

import {
  DATADOG_APPLICATION_ID,
  DATADOG_CLIENT_TOKEN,
  VERSION
} from '../utils/config'
import { debug } from '../utils/debug'
import { resolveEmbedType } from '../utils/embed'
import { env } from '../utils/env'
import { TRACE_KEYS, getTraceId } from '../utils/trace'

/**
 * Datadog RUM CDN URL.
 *
 * NOTE: This is the EU region URL. Sync with URL in index.html.
 */
const DATADOG_RUM_CDN_URL =
  'https://www.datadoghq-browser-agent.com/eu1/v5/datadog-rum.js'

/**
 * Injects the Datadog RUM CDN script into the page, mirroring the inline
 * `<script>` in index.html. This is needed for the web component entry
 * (embed.ts) where index.html is not loaded.
 *
 * Sets up `window.DD_RUM` with an `onReady` queue so that `initDatadog`
 * can schedule its init callback before the script finishes loading.
 */
export const loadDatadogScript = () => {
  // Already loaded (e.g. index.html inline script or a previous call)
  if (typeof window.DD_RUM !== 'undefined') return

  // Set up the onReady queue stub — same pattern as the DD CDN async snippet
  const stub = {
    q: [] as Array<() => void>,
    onReady(c: () => void) {
      stub.q.push(c)
    }
  }

  window.DD_RUM = stub as unknown as typeof window.DD_RUM

  const script = document.createElement('script')

  script.async = true
  script.src = DATADOG_RUM_CDN_URL
  document.head.appendChild(script)
}

export const initDatadog = ({
  /** Whether we should skip DD initialization */
  disabled = false,
  /** What mode we are initializing for */
  mode = 'index'
}: {
  disabled?: boolean
  mode?: 'index' | 'embed'
}) => {
  if (disabled) {
    debug('[Datadog] Skipped initialization')

    return
  }

  debug('[Datadog] Initializing')

  window.DD_RUM?.onReady(() => {
    try {
      // shouldn't happen, mostly coercing TS
      if (!isDatadogAvailable(window.DD_RUM)) return

      window.DD_RUM.init({
        applicationId: DATADOG_APPLICATION_ID,
        clientToken: DATADOG_CLIENT_TOKEN,
        site: 'datadoghq.eu',
        service: 'journey-app-v3',
        env: env('REACT_APP_STAGE') ?? 'unknown',
        version: VERSION,
        trackResources: true, // resource sizes and timing
        trackUserInteractions: false, // no need at this point
        trackViewsManually: true, // we want manual tracking (i.e. step navigation)
        trackLongTasks: true, // tasks that block the UI thread for longer periods
        usePartitionedCrossSiteSessionCookie: true,
        allowFallbackToLocalStorage: true,
        defaultPrivacyLevel: 'mask-user-input',
        enableExperimentalFeatures: ['feature_flags'],
        trackingConsent: 'not-granted',
        // enrich event data with journeyId
        beforeSend: (event, _context) => {
          if (event.type === 'error') {
            const error = event.error

            // filter out network errors, including the ones of 'AxiosError' type
            if (error.message.includes('NetworkError')) return false
          }

          event.context = {
            ...event.context,
            // Adds in the journey App's sessinon id
            journeySessionId: getTraceId(TRACE_KEYS.JOURNEY_SESSION_ID)
          }

          return true
        }
      })

      captureBrowserSize()
      captureDarkMode()
      captureEmbedContext(mode)

      debug('[Datadog] Initialized')
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('[Datadog] Failed to initialise', error)
      // fail gracefully
    }
  })
}

export const captureBrowserSize = () => {
  try {
    const round = (number: number) => Math.round(number / 100) * 100

    const width = round(window.innerWidth || document.body.clientWidth)
    const height = round(window.innerHeight || document.body.clientHeight)
    const size = width + 'x' + height

    getDatadogClient()?.setGlobalContextProperty('browserSize', size)
  } catch (_error) {
    // eslint-disable-next-line no-console
    console.warn('[Datadog] Failed to measure browser size')
    // fail gracefully
  }
}

/**
 * Sets the embed context as Datadog global context properties.
 * Uses `withDatadogRetries` so it works even when DD loads later
 * (e.g. web component path where DD is loaded after journey config).
 */
export const captureEmbedContext = (mode: 'index' | 'embed') => {
  const embedType = resolveEmbedType(mode)
  const hostPageUrl = document.referrer || window.location.href

  withDatadogRetries((client) => {
    client.setGlobalContextProperty('embedType', embedType)
    client.setGlobalContextProperty('hostPageUrl', hostPageUrl)

    debug('[Datadog] Embed context captured', { embedType })
  })
}

export const captureDarkMode = () => {
  try {
    if (typeof window.matchMedia === 'function') {
      const enabled = window.matchMedia('(prefers-color-scheme: dark)').matches

      getDatadogClient()?.setGlobalContextProperty('darkMode', enabled)
    }
  } catch (_error) {
    // eslint-disable-next-line no-console
    console.warn('[Datadog] Failed to capture dark mode')
    // fail gracefully
  }
}

/**
 * Return the DataDog Session Id.
 *
 * NOTE: If DataDog is not initialized, this will return `undefined`.
 */
export const getDataDogSessionId = () => {
  try {
    return (
      isDatadogAvailable(window.DD_RUM) &&
      window.DD_RUM?.getInternalContext()?.session_id
    )
  } catch (originalError) {
    // eslint-disable-next-line no-console
    return undefined
  }
}

/**
 * Determines if Datadog is initialized.
 */
export const isDatadogAvailable = (
  arg: typeof window.DD_RUM
): arg is RumGlobal => {
  try {
    return (
      typeof arg !== 'undefined' &&
      typeof (arg as RumGlobal).init === 'function'
    )
  } catch (originalError) {
    // eslint-disable-next-line no-console
    console.error('[Datadog] Failed to check on Datadog', originalError)

    return false
  }
}

/**
 * Returns Datadog instance, only if it had been initialized.
 */
export const getDatadogClient = () => {
  if (isDatadogAvailable(window.DD_RUM)) return window.DD_RUM

  return undefined
}

/**
 * Generic wrapper to retry a function until Datadog is available,
 * or the maximum number of retries is reached.
 *
 * Returns a cancel function in case needed to stop the retries.
 */
export const withDatadogRetries = (
  /** Callback to be executed when Datadog client is available */
  fn: (client: RumGlobal) => void,
  options?: {
    /**
     * Base timeout in miliseconds
     * @default 100
     */
    timeoutMs?: number
    /**
     * Maximum number of retries before permanently failing
     * @default 10
     */
    maxRetries?: number
    /**
     * Maximum waiting time before retrying
     * @default undefined
     */
    maxWaitMs?: number
    /**
     * Which backoff strategy to use
     * @default {@link backOffFibonacci}
     */
    backOffHandler?: BackOffHandler
  }
) => {
  const {
    backOffHandler = backOffFibonacci,
    maxRetries = 10,
    maxWaitMs,
    timeoutMs = 100
  } = options ?? {}

  let timeoutRef: number | undefined = undefined

  const wrapper = (retries: number = 1) => {
    if (isDatadogAvailable(window.DD_RUM)) {
      fn(window.DD_RUM)
    } else if (retries <= maxRetries) {
      const nextRetries = retries + 1

      timeoutRef = window.setTimeout(
        () => wrapper(nextRetries),
        backOffHandler(timeoutMs, nextRetries, maxWaitMs)
      )
    } else {
      // eslint-disable-next-line no-console
      console.warn(
        new Error(
          '[Datadog] withDatadogRetries permanently failed after retries'
        )
      )
    }
  }

  try {
    wrapper()
  } catch (originalError) {
    // fail gracefully
    // eslint-disable-next-line no-console
    console.warn(
      new Error(
        '[Datadog] withDatadogRetries caused an error',
        originalError instanceof Error ? originalError : undefined
      )
    )
  }

  // returns a cancel function
  return () => {
    window.clearTimeout(timeoutRef)
  }
}

/**
 * Synchronous error utility meant to register errors in Datadog by throwing
 * `console.error` but also registering directly to ensure an additional argument
 * gets ingested as `context` for the error.
 */
export const errorOut = (...args: Parameters<typeof console.error>) => {
  // eslint-disable-next-line no-console
  console.error(...args)
  getDatadogClient()?.addError(args[0], args[1])
}
